import { inject, observer } from 'mobx-react'
import PropTypes from 'prop-types'
import useMediaQuery from '@mui/material/useMediaQuery'

import { HelpIcon, TooltipText } from '../../../../components'
import { numberToDollars } from '../../../../utils'
import { DEFAULT_RETIREMENT_INCOME_GOAL_PCT } from '../../../../models/Person'
import { TABLET_VIEWPORT } from '../../../../guided-toolbox/utils/breakpoints'
import { StepContent, SkipToSummaryLink } from '../../elements'
import { getHeadingMessage, INCOME_TOKEN } from './headingMessage'
import { getSubheadingMessage } from './subheadingMessage'
import IncomeCharts from './IncomeCharts'
import {
  Heading,
  Amount,
  Subheading,
  LastWord,
  HelpAnchor,
  SkipRow,
  SkipToSummaryButton,
} from './style'

// Matches the summary's compact breakpoint so both advice steps collapse at the same width.
const CHART_COLLAPSE_VIEWPORT = '(max-width: 799px)'

function HeadingText({ template, incomeText }) {
  const [before, after] = template.split(INCOME_TOKEN)

  return (
    <>
      {before}
      {after !== undefined && (
        <>
          <Amount>{incomeText}</Amount>
          {after}
        </>
      )}
    </>
  )
}

function MonthlyRetirementIncome({ store, onSkipToSummary }) {
  // Tablet takes the mobile treatment: the button below the charts, not the link above.
  const skipBelowCharts = useMediaQuery(TABLET_VIEWPORT)
  const chartsCollapsed = useMediaQuery(CHART_COLLAPSE_VIEWPORT)
  const { baseCase, recommendedCase, person } = store

  // The shell shows the loader; this just guards a transient render before the cases resolve.
  if (!baseCase || !recommendedCase) {
    return null
  }

  const modifiedCase = store.modifiedCase
  const plan = modifiedCase || recommendedCase
  const isModified = !!modifiedCase
  const goalPercent = person?.retirementIncomeGoalPct ?? DEFAULT_RETIREMENT_INCOME_GOAL_PCT

  const headingTemplate = getHeadingMessage({
    isModified,
    currentOnTrack: baseCase.isOnTrack,
    planOnTrack: plan.isOnTrack,
    planImproved: plan.retirementIncomeGoal > baseCase.retirementIncomeGoal,
  })

  const subheading = getSubheadingMessage({
    achievedPercent: plan.retirementIncomeGoal,
    planOnTrack: plan.isOnTrack,
  })

  const incomeText = `$${numberToDollars(plan.projectedMonthlyRetirementInc, true)}`

  // Keep the help icon with the last word so it cannot wrap onto its own line.
  const lastSpace = subheading.lastIndexOf(' ')
  const subheadingLead = subheading.slice(0, lastSpace + 1)
  const subheadingLastWord = subheading.slice(lastSpace + 1)

  return (
    <StepContent>
      <Heading>
        <HeadingText template={headingTemplate} incomeText={incomeText} />
      </Heading>

      <Subheading>
        {subheadingLead}
        <LastWord>
          {subheadingLastWord}
          <HelpAnchor>
            <HelpIcon tooltip={TooltipText.replacementIncome()} size='mediumLarge' />
          </HelpAnchor>
        </LastWord>
      </Subheading>

      {!skipBelowCharts && (
        <SkipRow>
          <SkipToSummaryLink onSkip={onSkipToSummary} />
        </SkipRow>
      )}

      <IncomeCharts
        current={baseCase}
        recommended={recommendedCase}
        modified={modifiedCase}
        goalPercent={goalPercent}
        collapsed={chartsCollapsed}
      />

      {/* Disabled without a handler, matching SkipToSummaryLink — that is how
          the flow locks this path while a response streams. */}
      {skipBelowCharts && (
        <SkipToSummaryButton type='button' onClick={onSkipToSummary} disabled={!onSkipToSummary}>
          Skip to summary
        </SkipToSummaryButton>
      )}
    </StepContent>
  )
}

MonthlyRetirementIncome.propTypes = {
  onSkipToSummary: PropTypes.func,
}

export default inject('store')(observer(MonthlyRetirementIncome))
